import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  DimensionOutroSummary,
  HistoricPatternScore,
  HistoricScaleScore,
  Nature,
  PatternScoreHistory,
  ScaleScoreHistory,
  TraitPlasticity,
} from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";

import styles from "./DeltaBreakdown.module.css";

import elementsJsonData from "../../../../../assets/traitInfo/elements.json";
import patternsJsonData from "../../../../../assets/traitInfo/patterns.json";

import { AuthHeaderWithSmallBackButton } from "@/components/shared/AuthenticatedHeader/AuthHeader";
import {
  dimensionData,
  dimensionIcons,
} from "@/assets/traitInfo/dimensionData";
import {
  getUserPatternScore,
  getUserScaleScore,
} from "@/components/traits/utils";
import Element from "@/components/traits/element/Element/Element";
import { ExtendedPattern, ExtendedScale } from "@/models/sharedModels";
import { sortByScoreThenNameForOutroBreakdown } from "@/components/assessments-new/assessmentUtils";
import HistoricalTrackingChart from "@/components/shared/HistoricalTrackingChart/HistoricalTrackingChart";
import TraitDescription from "@/components/traits/shared/TraitScoreTable/TraitDescription";
import { natureMap } from "@/assets/traitInfo/natureData";
import ThemedButton from "@/components/shared/ThemedButton/ThemedButton";
import { Mixpanel } from "@/helpers/mixpanel";
import { useAuth } from "@/context/AuthContext";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import { useModalContext } from "@/context/ModalContext";
import ElementModal from "@/components/traits/TraitsModals/Element/ElementModal";

const font = Roboto_Mono({ subsets: ["latin"] });

type Props = {
  outro: DimensionOutroSummary | null;
  slug: string;
  onFinish: () => void;
  onBack: () => void;
  source: "assessment completion" | "latest results item";
};

type PropsTraitBreakdownPattern = {
  slug: string;
  pattern: ExtendedPattern | undefined;
  onFinish: () => void;
  onBack: () => void;
  source: "assessment completion" | "latest results item";
};

type PropsTraitBreakdownElement = {
  slug: string;
  element: ExtendedScale | undefined;
  onFinish: () => void;
  onBack: () => void;
  source: "assessment completion" | "latest results item";
};

export default function DeltaBreakdown(props: Props) {
  const { user } = useAuth();

  const { setModalComponent } = useModalContext();

  const elementsDiscovered = useSelector(
    (state: RootState) => state.user
  ).elementsDiscovered;

  const [currentElement, setCurrentElement] = useState<ExtendedScale | null>(
    null
  );

  const historicScores = useGetScoreHistory(currentElement?.slug ?? "");

  const dimension = dimensionData.find(
    (d) => d.slug === props.slug || d.longFormSlug === props.slug
  );

  useEffect(() => {
    Mixpanel?.track(
      "Full Tracked Dimension Breakdown Viewed",
      {
        source: props.source,
        slug: props.slug,
      },
      user?.uid
    );
  }, []);

  useEffect(() => {
    const firstElement = props.outro?.elementDiscoveries.sort(
      sortByScoreThenNameForOutroBreakdown
    )[0];

    if (firstElement) {
      const foundElement = elementsJsonData.find(
        (e) => e.slug === firstElement.scaleSlug
      ) as unknown as ExtendedScale;

      if (foundElement) {
        setCurrentElement(foundElement);
      }
    }
  }, []);

  const mappedElements = props.outro?.elementDiscoveries
    .sort(sortByScoreThenNameForOutroBreakdown)
    .map((element) => {
      return (
        <div
          onClick={() => {
            if (window.Android?.onHaptic) {
              window.Android.onHaptic();
            }
            const foundElement = elementsJsonData.find(
              (e) => e.slug === element.scaleSlug
            ) as unknown as ExtendedScale;
            if (foundElement) {
              setCurrentElement(foundElement);
            }
          }}
          className={styles.elementDiv}
          key={element.scaleSlug}
          style={{
            opacity: currentElement?.slug === element.scaleSlug ? "1" : "0.4",
          }}
        >
          <Element
            slug={element.scaleSlug}
            score={getUserScaleScore(
              element.scaleScore?.percentScore,
              element.scaleSlug,
              element.scaleScore?.dimensionSlugSource
            )}
          />
        </div>
      );
    });

  return (
    <main className={styles.main}>
      <AuthHeaderWithSmallBackButton
        title="Full breakdown"
        subTitle={dimension?.title}
        onBack={() => props.onBack()}
        backButton
        sticky
      />

      <section className={styles.top}>
        <div className={styles.iconDiv}>{dimensionIcons[props.slug].icon}</div>
      </section>

      <section className={styles.dimensionDescriptionSection}>
        <h2 className={styles.dimensionTitle}>{dimension?.title}</h2>
        <p className={styles.dimensionDescription}>{dimension?.description}</p>
      </section>

      <section className={styles.wrapperSection}>{mappedElements}</section>

      <section className={styles.chartSection}>
        <h3 className={styles.elementTitle}>
          {currentElement?.alias ?? currentElement?.name}
        </h3>
        <p className={styles.elementDescription}>
          {currentElement?.shortDescription}
        </p>

        <div className={styles.chartDiv}>
          <HistoricalTrackingChart
            type="element"
            slug={currentElement?.slug ?? ""}
            percentScore={
              elementsDiscovered?.find(
                (e) => e.scaleSlug === currentElement?.slug
              )?.scaleScore?.percentScore
            }
            nature={currentElement?.nature}
            isPremium={currentElement?.isPremium}
            handleSwipe={(v) => {
              return;
            }}
            scoreHistory={historicScores}
            noMargin
            noTitle
            dimensionSlugSource={
              elementsDiscovered?.find(
                (e) => e.scaleSlug === currentElement?.slug
              )?.scaleScore?.dimensionSlugSource
            }
          />
        </div>

        <div className={styles.tableParent}>
          <TraitDescription
            type="Element"
            dimension={currentElement?.dimensionSlug}
            nature={
              currentElement?.nature
                ? natureMap[currentElement?.nature].name
                : undefined
            }
            color={
              currentElement?.nature
                ? natureMap[currentElement?.nature].color
                : undefined
            }
            plasticity={currentElement?.plasticity}
            averageUserScore={currentElement?.traitContent?.averagePercentScore}
            yourScoreToday={getUserScaleScore(
              elementsDiscovered?.find(
                (e) => e.scaleSlug === currentElement?.slug
              )?.scaleScore?.percentScore,
              currentElement?.slug ?? "",
              elementsDiscovered?.find(
                (e) => e.scaleSlug === currentElement?.slug
              )?.scaleScore?.dimensionSlugSource
            )}
          />
        </div>
        <div
          onClick={() =>
            setModalComponent(
              <ElementModal slug={currentElement?.slug ?? ""} />
            )
          }
          className={styles.viewFullTraitDiv}
        >
          <p className={`${font.className} ${styles.viewFullTraitText}`}>
            View full trait
          </p>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M1.51496 15.3516H13.2019C13.6609 15.3516 14.0367 14.9758 14.0367 14.5169V7.8386H12.3671V13.6821H2.34974V3.66468H8.19322V1.99512H1.51496C1.056 1.99512 0.680176 2.37094 0.680176 2.8299V14.5169C0.680176 14.9758 1.056 15.3516 1.51496 15.3516Z"
              fill="#928E8C"
            />
            <path
              d="M7.12842 7.75436L8.29745 8.92339L13.7022 3.53883V5.35432H15.3718V0.700195H10.6974V2.36976H12.5129L7.12842 7.75436Z"
              fill="#928E8C"
            />
          </svg>
        </div>
      </section>
      <section className={styles.bottomButton}>
        <ThemedButton
          style={{ fontWeight: "450", width: "100%" }}
          title="Complete"
          onClick={() => props.onFinish()}
        />
      </section>
    </main>
  );
}

export function DeltaBreakdownTraitAssessmentPattern(
  props: PropsTraitBreakdownPattern
) {
  const { user } = useAuth();

  useEffect(() => {
    Mixpanel?.track(
      "Full Tracked Trait Breakdown Viewed",
      {
        source: props.source,
        slug: props.slug,
      },
      user?.uid
    );
  }, []);

  const [selectedTrait, setSelectedTrait] = useState<{
    traitType: "element" | "pattern";
    slug: string;
  }>();

  const currentElement = elementsJsonData.find(
    (e) => e.slug === selectedTrait?.slug
  );

  useEffect(() => {
    setSelectedTrait({ traitType: "pattern", slug: props.slug });
  }, [props.slug]);

  const elementsDiscovered = useSelector(
    (state: RootState) => state.user
  ).elementsDiscovered;

  const patternsDiscovered = useSelector(
    (state: RootState) => state.user
  ).patternsDiscovered;

  const pattern = patternsDiscovered?.find((p) => p.patternSlug == props.slug);
  const patternComponents = patternsJsonData
    .find((p) => p.slug == props.slug)
    ?.blendedRules.map((el) => {
      return {
        traitType: "element",
        slug: el.slug,
      };
    });

  const mapped = [
    { traitType: "pattern", slug: pattern?.patternSlug },
    patternComponents,
  ]
    .flat()
    .map((trait) => {
      if (trait && trait.traitType === "pattern" && trait.slug) {
        return (
          <div
            onClick={() => {
              if (window.Android?.onHaptic) {
                window.Android.onHaptic();
              }
              if (trait.slug) {
                setSelectedTrait({ traitType: "pattern", slug: trait.slug });
              }
              // const foundElement = elementsJsonData.find(
              //   (e) => e.slug === element.scaleSlug
              // ) as unknown as ExtendedScale;
              // if (foundElement) {
              //   setCurrentElement(foundElement);
              // }
            }}
            className={styles.elementDiv}
            key={trait.slug}
            style={{
              opacity: selectedTrait?.slug === trait.slug ? "1" : "0.4",
            }}
          >
            <Pattern
              score={getUserPatternScore(
                pattern?.percentScore,
                props.slug,
                pattern?.isLongFormGenerated
              )}
              patternSlug={trait.slug}
            />
          </div>
        );
      }

      if (trait && trait.traitType === "element" && trait.slug) {
        const disocveredElement = elementsDiscovered?.find(
          (e) => e.scaleSlug === trait.slug
        );
        return (
          <div
            onClick={() => {
              if (window.Android?.onHaptic) {
                window.Android.onHaptic();
              }
              const foundElement = elementsJsonData.find(
                (e) => e.slug === trait.slug
              ) as unknown as ExtendedScale;
              if (trait.slug) {
                setSelectedTrait({ traitType: "element", slug: trait.slug });
              }
              // if (foundElement) {
              //   setCurrentElement(foundElement);
              // }
            }}
            className={styles.elementDiv}
            key={trait.slug}
            style={{
              opacity: selectedTrait?.slug === trait.slug ? "1" : "0.4",
            }}
          >
            <Element
              slug={trait.slug}
              score={
                disocveredElement
                  ? getUserScaleScore(
                      disocveredElement.scaleScore?.percentScore,
                      disocveredElement.scaleSlug,
                      disocveredElement.scaleScore?.dimensionSlugSource
                    )
                  : null
              }
            />
          </div>
        );
      }
    });

  const scoreHistoryElement = useGetScoreHistory(selectedTrait?.slug ?? "");
  const scoreHistoryPattern = useGetScoreHistoryPattern(
    selectedTrait?.slug ?? ""
  );

  const scoreHistory =
    selectedTrait?.traitType === "element"
      ? scoreHistoryElement
      : scoreHistoryPattern;

  const trackingDescription =
    props.slug in mappedPatternBreakdownDescription
      ? mappedPatternBreakdownDescription[
          props.slug as keyof typeof mappedPatternBreakdownDescription
        ]
      : null;

  return (
    <main className={styles.main}>
      <AuthHeaderWithSmallBackButton
        title="Personality check-in"
        subTitle={props.pattern?.name}
        onBack={() => props.onBack()}
        backButton
        sticky
      />

      <section
        style={{
          alignItems: "flex-start",
          padding: "30px 15px 0 15px",
          gap: "10px",
        }}
        className={styles.top}
      >
        <h1 style={{ padding: "0" }} className={styles.dimensionTitle}>
          {props.pattern?.name}
        </h1>
        {trackingDescription && (
          <p className={styles.dimensionDescription}>{trackingDescription}</p>
        )}
      </section>

      <section style={{ marginTop: "40px" }} className={styles.wrapperSection}>
        {mapped}
      </section>

      <section className={styles.chartSection}>
        <h3 className={styles.elementTitle}>
          {selectedTrait?.traitType === "element"
            ? currentElement?.alias ?? currentElement?.name
            : props.pattern?.name}
        </h3>
        <p className={styles.elementDescription}>
          {selectedTrait?.traitType === "element"
            ? currentElement?.shortDescription
            : props.pattern?.shortDescription}
        </p>

        <div className={styles.chartDiv}>
          <HistoricalTrackingChart
            type={
              selectedTrait?.traitType === "element" ? "element" : "pattern"
            }
            slug={selectedTrait?.slug ?? ""}
            percentScore={
              selectedTrait?.traitType === "element"
                ? elementsDiscovered?.find(
                    (e) => e.scaleSlug === currentElement?.slug
                  )?.scaleScore?.percentScore
                : pattern?.patternScore
            }
            nature={
              selectedTrait?.traitType === "element"
                ? (currentElement?.nature as Nature)
                : props.pattern?.nature
            }
            isPremium={
              selectedTrait?.traitType === "element"
                ? currentElement?.isPremium
                : props.pattern?.isPremium
            }
            handleSwipe={(v) => {
              return;
            }}
            dimensionSlugSource={
              selectedTrait?.traitType === "element"
                ? elementsDiscovered?.find(
                    (e) => e.scaleSlug === currentElement?.slug
                  )?.scaleScore?.dimensionSlugSource
                : null
            }
            isLongFormGenerated={
              selectedTrait?.traitType === "element"
                ? null
                : pattern?.isLongFormGenerated
            }
            scoreHistory={scoreHistory}
            noMargin
            noTitle
          />
        </div>

        <div className={styles.tableParent}>
          <TraitDescription
            type={
              selectedTrait?.traitType === "element" ? "Element" : "Pattern"
            }
            nature={
              selectedTrait?.traitType === "element"
                ? currentElement?.nature
                  ? natureMap[currentElement?.nature as keyof typeof natureMap]
                      .name
                  : undefined
                : props.pattern?.nature
                ? natureMap[props.pattern?.nature as keyof typeof natureMap]
                    .name
                : undefined
            }
            color={
              selectedTrait?.traitType === "element"
                ? currentElement?.nature
                  ? natureMap[currentElement?.nature as keyof typeof natureMap]
                      .color
                  : undefined
                : props.pattern?.nature
                ? natureMap[props.pattern?.nature as keyof typeof natureMap]
                    .color
                : undefined
            }
            plasticity={
              selectedTrait?.traitType === "element"
                ? (currentElement?.plasticity as
                    | TraitPlasticity
                    | null
                    | undefined)
                : props.pattern?.plasticity
            }
            averageUserScore={
              selectedTrait?.traitType === "element"
                ? currentElement?.traitContent?.averagePercentScore
                : props.pattern?.traitContent.averagePercentScore
            }
            yourScoreToday={
              selectedTrait?.traitType === "element"
                ? getUserScaleScore(
                    elementsDiscovered?.find(
                      (e) => e.scaleSlug === currentElement?.slug
                    )?.scaleScore?.percentScore,
                    currentElement?.slug ?? "",
                    elementsDiscovered?.find(
                      (e) => e.scaleSlug === currentElement?.slug
                    )?.scaleScore?.dimensionSlugSource
                  )
                : getUserPatternScore(
                    pattern?.percentScore,
                    pattern?.patternSlug as string,
                    pattern?.isLongFormGenerated
                  )
            }
            patternGroupSlug={props.pattern?.patternGroupSlug}
          />
        </div>
      </section>
      <section className={styles.bottomButton}>
        <ThemedButton
          style={{ fontWeight: "450", width: "100%" }}
          title="Complete"
          onClick={() => props.onFinish()}
        />
      </section>
    </main>
  );
}

export function DeltaBreakdownTraitAssessmentElement(
  props: PropsTraitBreakdownElement
) {
  const elementsDiscovered = useSelector(
    (state: RootState) => state.user
  ).elementsDiscovered;

  const historicScores = useGetScoreHistory(props.slug);

  return (
    <main className={styles.main}>
      <AuthHeaderWithSmallBackButton
        title="Personality check-in"
        subTitle={props.element?.alias ?? props.element?.name}
        onBack={() => props.onBack()}
        backButton
        sticky
      />
      <section
        style={{
          alignItems: "flex-start",
          padding: "20px 15px 0 15px",
          gap: "10px",
        }}
        className={styles.top}
      >
        <Element slug={props.slug} />
      </section>

      <section className={styles.chartSection}>
        <h3 className={styles.elementTitle}>
          {props.element?.alias ?? props.element?.name}
        </h3>
        <p className={styles.elementDescription}>
          {props.element?.shortDescription}
        </p>

        <div className={styles.chartDiv}>
          <HistoricalTrackingChart
            type="element"
            slug={props.element?.slug ?? ""}
            percentScore={
              elementsDiscovered?.find(
                (e) => e.scaleSlug === props.element?.slug
              )?.scaleScore?.percentScore
            }
            nature={props.element?.nature}
            isPremium={props.element?.isPremium}
            handleSwipe={(v) => {
              return;
            }}
            dimensionSlugSource={
              elementsDiscovered?.find(
                (e) => e.scaleSlug === props.element?.slug
              )?.scaleScore?.dimensionSlugSource
            }
            scoreHistory={historicScores}
            noMargin
            noTitle
          />
        </div>

        <div className={styles.tableParent}>
          <TraitDescription
            type="Element"
            dimension={props.element?.dimensionSlug}
            nature={
              props.element?.nature
                ? natureMap[props.element?.nature].name
                : undefined
            }
            color={
              props.element?.nature
                ? natureMap[props.element?.nature].color
                : undefined
            }
            plasticity={props.element?.plasticity}
            averageUserScore={props.element?.traitContent?.averagePercentScore}
            yourScoreToday={getUserScaleScore(
              elementsDiscovered?.find(
                (e) => e.scaleSlug === props.element?.slug
              )?.scaleScore?.percentScore,
              props.element?.slug ?? "",
              elementsDiscovered?.find(
                (e) => e.scaleSlug === props.element?.slug
              )?.scaleScore?.dimensionSlugSource
            )}
          />
        </div>
      </section>
      <section className={styles.bottomButton}>
        <ThemedButton
          style={{ fontWeight: "450", width: "100%" }}
          title="Complete"
          onClick={() => props.onFinish()}
        />
      </section>
    </main>
  );
}

function useGetScoreHistory(slug: string): ScaleScoreHistory {
  const [historicScores, setHistoricScores] = useState<
    HistoricScaleScore[] | null | undefined
  >(null);

  const element = useSelector(
    (state: RootState) => state.user
  ).elementsDiscovered?.find((el) => el.scaleSlug === slug);

  useEffect(() => {
    if (element?.scaleScore?.scoreHistory?.historicScores?.length) {
      setHistoricScores(element?.scaleScore?.scoreHistory?.historicScores);
    } else {
      if (element?.scaleScore?.percentScore) {
        setHistoricScores([
          {
            createdOn: element?.createdOn ?? Date.now(),
            dimensionSlugSource: element.scaleScore.dimensionSlugSource,
            percentScore: element.scaleScore.percentScore,
            intraRawRank: element.scaleScore.intraRawRank,
            standardizedRankScore: element.scaleScore.standardizedRankScore,
          },
        ]);
      }
    }
  }, [element?.scaleScore]);

  return { historicScores: historicScores };
}

function useGetScoreHistoryPattern(
  slug: string | undefined
): PatternScoreHistory {
  const [historicScores, setHistoricScores] = useState<
    HistoricPatternScore[] | null | undefined
  >(null);

  const pattern = useSelector(
    (state: RootState) => state.user
  ).patternsDiscovered?.find((p) => p.patternSlug === slug);

  useEffect(() => {
    if (pattern?.scoreHistory?.historicScores?.length) {
      setHistoricScores(pattern?.scoreHistory?.historicScores);
    } else {
      if (pattern?.percentScore) {
        setHistoricScores([
          {
            createdOn: pattern?.createdOn ?? Date.now(),
            percentScore: pattern?.percentScore,
            isLongFormGenerated: pattern?.isLongFormGenerated,
          },
        ]);
      }
    }
  }, [pattern?.percentScore]);

  return { historicScores: historicScores };
}

const mappedPatternBreakdownDescription = {
  "pattern-neo-agreeableness":
    "One of the Big Five personality traits. Agreeable people are kind, sympathetic, and cooperative and Prefer social harmony to social ascendance.",
  "pattern-neo-openness":
    "One of the Big Five personality traits. Open people are imaginative, curious, and willing to explore new ideas and experiences, valuing creativity and intellectual depth.",
  "pattern-neo-conscientiousness":
    "One of the Big Five personality traits. Conscientious people are disciplined, organized, and goal-oriented, prioritizing responsibility and long-term success over impulsivity.",
  "pattern-neo-extraversion":
    "One of the Big Five personality traits. Extraverted people are outgoing, energetic, and socially engaged, thriving on interaction and stimulation rather than solitude.",
  "pattern-neo-emotional-stability":
    "One of the Big Five personality traits. Emotionally stable people are calm, resilient, and less prone to stress, tending to experience fewer negative emotions in response to life's challenges.",
  "pattern-self-criticism":
    "Tendency to experience guilt and shame in response to  actions and self-perception. Guilt reflects a focus on specific behaviors and a motivation to make amends, while shame involves a deeper sense of personal inadequacy and self-condemnation.",
};
